import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { DsButtonModule } from '@bmw-ds/components';

import { ConsentBoxContentComponent } from '../../../consent/components/consent-box-content-column/consent-box-content.component';
import { ConsentMapper } from '../../../consent/mapper/consent.mapper';
import { ConsentSelection, ConsentType } from '../../../consent/model/consent-checkbox';
import { ConsentProvider } from '../../../consent/services/consent.provider';
import { DealerConsentService } from '../../../consent/services/dealer-consent.service';
import { useI18n } from '../../../i18n/services/i18n.service';
import { ButtonRowComponent } from '../../../modal/button/button-row.component';
import { BrandedIdType } from '../../model/branded-id-type';
import { ConsentsFormData } from '../../model/consents-form-data';
import { CreateBmwMiniIdRadioButtonsComponent } from '../create-bmw-mini-id-radio-buttons/create-bmw-mini-id-radio-buttons.component';

@Component({
  selector: 'cdf-customer-consents-form',
  imports: [ConsentBoxContentComponent, CreateBmwMiniIdRadioButtonsComponent, DsButtonModule, ButtonRowComponent],
  template: `
    <cdf-consent-box-content
      [customerLanguage]="customerLanguage()"
      [(consentSelection)]="consentValues"
      [consentCheckboxGroups]="checkboxGroups()"
      [legalText]="consentProvider.nscLegalText()"
      [consentType]="consentType()"
      [consentMetadata]="consentProvider.metadata()"
      [initialConsentCreationMode]="consentType() === ConsentType.NSC"
    />
    @if (consentType() === ConsentType.NSC) {
      <hr class="my-4x" />
      <cdf-create-bmw-mini-id-radio-buttons [(brandedIdType)]="brandedIdType" [bmwMiniId]="bmwMiniId()" />
    }
    <cdf-button-row class="mt-4x">
      <a class="align-self-center" (click)="skip.emit()">{{ i18n('button.skip') }}</a>
      <ds-button ds-loading [isLoading]="saving()" (click)="save()">
        {{ i18n(consentType() === ConsentType.NSC ? 'button.saveAndSendToCustomer' : 'button.saveChanges') }}
      </ds-button>
    </cdf-button-row>
  `,
  styleUrl: './customer-consents-form.container.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerConsentsFormContainer {
  protected readonly i18n = useI18n();
  protected readonly consentProvider = inject(ConsentProvider);
  private readonly consentMapper = inject(ConsentMapper);
  private readonly dealerConsentService = inject(DealerConsentService);

  readonly customerLanguage = input<string | undefined>();
  readonly bmwMiniId = input<string>();
  readonly consentType = input.required<ConsentType>();

  protected readonly consentValues = signal<ConsentSelection>({});
  protected readonly brandedIdType = signal(BrandedIdType.NONE);

  readonly saving = input<boolean>(false);
  readonly skip = output();
  readonly saveAndSend = output<ConsentsFormData>();

  protected readonly dealerConsentSelection = signal<Record<string, boolean>>(
    this.dealerConsentService.getDefaultDealerConsent(),
  );

  protected save() {
    const consentValues = { ...this.consentValues() };

    // Add implicit dealer consents if any consents are selected
    if (this.consentType() === ConsentType.DEALER && Object.entries(consentValues).length > 0) {
      consentValues['dataProcessing'] = true;
    }

    this.saveAndSend.emit({ brandedIdType: this.brandedIdType(), consentValues });
  }

  protected readonly legalText = computed(() =>
    this.consentType() === ConsentType.NSC
      ? this.consentProvider.nscLegalText()
      : this.dealerConsentService.dealerLegalText(),
  );

  protected readonly dealerConsentCheckboxes = computed(() => {
    return this.consentMapper.mapToDealerConsent(this.dealerConsentSelection(), ['dataProcessing']);
  });

  protected readonly checkboxGroups = computed(() => {
    if (this.consentType() === ConsentType.NSC) {
      return this.consentMapper.mapToConsentCheckboxGroups(this.consentProvider.nonImplicitNscConsents());
    }

    return this.dealerConsentCheckboxes();
  });

  protected readonly ConsentType = ConsentType;
}
